"use client";

import React, { useState, useEffect, useMemo } from "react";
import { Search, X, ArrowRight, Sparkles, SlidersHorizontal } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import {
  EcommerceProductsProvider,
  useEcommerceProducts,
  AddToCartButton,
  ProductSortDropdown,
  ProductsFilter,
  ProductPrice,
  EcommerceProduct,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";

type Language = "en" | "zh" | "fr" | "es" | "de";

function useWvcLanguage(): Language {
  const [lang, setLang] = useState<Language>("en");
  useEffect(() => {
    try {
      const detected =
        document.documentElement.lang?.slice(0, 2).toLowerCase() ||
        (typeof window !== "undefined" &&
          (window as any).wvcClient?.getLanguage?.()?.slice(0, 2)?.toLowerCase()) ||
        "en";
      if (["en", "zh", "fr", "es", "de"].includes(detected)) {
        setLang(detected as Language);
      }
    } catch {
      // fallback to en
    }
  }, []);
  return lang;
}

const translations: Record<
  Language,
  {
    catalogueSearch: string;
    findSignature: string;
    searchDescription: string;
    searchPlaceholder: string;
    popular: string;
    search: string;
    addBag: string;
    sale: string;
    noResults: string;
    noMatches: string;
    exploreEdits: string;
    refineTerms: string;
    suggestedTerms: string;
    curatedCategories: string;
    trendingThisSeason: string;
    viewFullCollection: string;
    loadMore: string;
    loading: string;
    allCollections: string;
    searchResults: string;
    refineSearch: string;
    filterCriteria: string;
    close: string;
  }
> = {
  en: {
    catalogueSearch: "Catalogue Search",
    findSignature: "Find your signature piece",
    searchDescription: "Search by silhouette, natural fabric, occasion, or seasonal capsule.",
    searchPlaceholder: "Search by keyword, e.g. Silk Dress, Linen Blazer...",
    popular: "Popular:",
    search: "Search",
    addBag: "Add to Bag",
    sale: "Sale",
    noResults: "No styles found matching your search",
    noMatches: "We couldn't find matches for",
    exploreEdits: "Explore our signature edits or search by fabric and silhouette.",
    refineTerms: "Refine your terms or discover pieces from our latest wardrobe curation.",
    suggestedTerms: "Suggested terms",
    curatedCategories: "Curated categories",
    trendingThisSeason: "Trending this season",
    viewFullCollection: "View Full Collection",
    loadMore: "Load More Styles",
    loading: "Loading...",
    allCollections: "All garment collections",
    searchResults: "Search results for",
    refineSearch: "Refine Search",
    filterCriteria: "Filter Criteria",
    close: "Close",
  },
  zh: {
    catalogueSearch: "目录搜索",
    findSignature: "寻找您的标志性单品",
    searchDescription: "按版型、天然面料、场合或当季胶囊系列进行搜索。",
    searchPlaceholder: "按关键词搜索，例如：真丝连衣裙、亚麻西装外套...",
    popular: "热门推荐：",
    search: "搜索",
    addBag: "加入购物袋",
    sale: "特惠",
    noResults: "未找到符合您搜索条件的款式",
    noMatches: "未找到与以下内容匹配的结果：",
    exploreEdits: "探索我们的标志性精选或按面料和剪裁搜索。",
    refineTerms: "优化您的关键词或探索我们最新衣橱精选单品。",
    suggestedTerms: "建议关键词",
    curatedCategories: "精选分类",
    trendingThisSeason: "本季流行",
    viewFullCollection: "查看完整系列",
    loadMore: "加载更多款式",
    loading: "加载中...",
    allCollections: "所有服饰系列",
    searchResults: "搜索结果：",
    refineSearch: "精准筛选",
    filterCriteria: "筛选条件",
    close: "关闭",
  },
  fr: {
    catalogueSearch: "Recherche de catalogue",
    findSignature: "Trouvez votre pièce signature",
    searchDescription: "Recherchez par silhouette, matière naturelle, occasion ou collection capsule de saison.",
    searchPlaceholder: "Recherche par mot-clé, ex. Robe en soie, Blazer en lin...",
    popular: "Populaire :",
    search: "Rechercher",
    addBag: "Ajouter au panier",
    sale: "Solde",
    noResults: "Aucun style trouvé correspondant à votre recherche",
    noMatches: "Nous n'avons trouvé aucun résultat pour",
    exploreEdits: "Explorez nos sélections signatures ou recherchez par matière et silhouette.",
    refineTerms: "Affinez vos termes ou découvrez les pièces de notre dernière sélection de garde-robe.",
    suggestedTerms: "Termes suggérés",
    curatedCategories: "Catégories sélectionnées",
    trendingThisSeason: "Tendances de la saison",
    viewFullCollection: "Voir toute la collection",
    loadMore: "Charger plus de styles",
    loading: "Chargement...",
    allCollections: "Toutes les collections de vêtements",
    searchResults: "Résultats de recherche pour",
    refineSearch: "Affiner la recherche",
    filterCriteria: "Critères de filtrage",
    close: "Fermer",
  },
  es: {
    catalogueSearch: "Búsqueda en el catálogo",
    findSignature: "Encuentra tu prenda insignia",
    searchDescription: "Busca por silueta, tejido natural, ocasión o colección cápsula de temporada.",
    searchPlaceholder: "Buscar por palabra clave, ej. Vestido de seda, Blazer de lino...",
    popular: "Popular:",
    search: "Buscar",
    addBag: "Añadir a la bolsa",
    sale: "Rebajas",
    noResults: "No se han encontrado estilos que coincidan con tu búsqueda",
    noMatches: "No pudimos encontrar coincidencias para",
    exploreEdits: "Explora nuestras selecciones exclusivas o busca por tejido y silueta.",
    refineTerms: "Refina tus términos o descubre piezas de nuestra última selección de armario.",
    suggestedTerms: "Términos sugeridos",
    curatedCategories: "Categorías seleccionadas",
    trendingThisSeason: "Tendencia esta temporada",
    viewFullCollection: "Ver colección completa",
    loadMore: "Cargar más estilos",
    loading: "Cargando...",
    allCollections: "Todas las colecciones de prendas",
    searchResults: "Resultados de búsqueda para",
    refineSearch: "Refinar búsqueda",
    filterCriteria: "Criterios de filtro",
    close: "Cerrar",
  },
  de: {
    catalogueSearch: "Katalogsuche",
    findSignature: "Finden Sie Ihr Signature-Piece",
    searchDescription: "Suchen Sie nach Silhouette, Naturstoff, Anlass oder saisonaler Kapsel.",
    searchPlaceholder: "Nach Stichwort suchen, z. B. Seidenkleid, Leinenblazer...",
    popular: "Beliebt:",
    search: "Suchen",
    addBag: "In die Tasche",
    sale: "Sale",
    noResults: "Keine passenden Styles für Ihre Suche gefunden",
    noMatches: "Wir konnten keine Treffer finden für",
    exploreEdits: "Entdecken Sie unsere Signature-Edits oder suchen Sie nach Stoff und Silhouette.",
    refineTerms: "Verfeinern Sie Ihre Begriffe oder entdecken Sie Stücke aus unserer neuesten Garderoben-Kuration.",
    suggestedTerms: "Vorgeschlagene Begriffe",
    curatedCategories: "Kuratierte Kategorien",
    trendingThisSeason: "Trends der Saison",
    viewFullCollection: "Gesamte Kollektion ansehen",
    loadMore: "Mehr Styles laden",
    loading: "Laden...",
    allCollections: "Alle Bekleidungskollektionen",
    searchResults: "Suchergebnisse für",
    refineSearch: "Suche verfeinern",
    filterCriteria: "Filterkriterien",
    close: "Schließen",
  },
};

const POPULAR_SUGGESTIONS = [
  "Silk Dress",
  "Linen Blazer",
  "High-waisted Trouser",
  "Cashmere Knit",
  "Tailored Coat",
];

const CURATED_CATEGORIES = [
  { name: "Dresses", path: "/ShopPage", count: "24 styles" },
  { name: "Outerwear", path: "/ShopPage", count: "16 styles" },
  { name: "Sets & Tailoring", path: "/ShopPage", count: "12 styles" },
  { name: "Tops & Silk", path: "/ShopPage", count: "19 styles" },
];

const FALLBACK_TRENDING = [
  {
    title: "Belted Silk Crepe Midi Dress",
    category: "Dresses",
    price: "£285",
    img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/trending_silk_dress.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/trending_silk_dress.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/trending_silk_dress.png-wvc-sizes",
  },
  {
    title: "Double-Breasted Wool Coat",
    category: "Outerwear",
    price: "£420",
    img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/trending_wool_coat.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/trending_wool_coat.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/trending_wool_coat.webp-wvc-sizes",
  },
  {
    title: "High-Waist Pleated Trouser",
    category: "Bottoms",
    price: "£195",
    img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/trending_pleated_trouser.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/trending_pleated_trouser.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/trending_pleated_trouser.webp-wvc-sizes",
  },
  {
    title: "Structured Linen Blazer",
    category: "Sets & Tailoring",
    price: "£310",
    img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/trending_linen_blazer.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/trending_linen_blazer.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/trending_linen_blazer.webp-wvc-sizes",
  },
];

function ProductGridCard({
  product,
  index,
  language,
}: {
  product: EcommerceProduct;
  index: number;
  language: Language;
}) {
  const t = translations[language];

  return (
    <article
      data-index={index}
      className="group flex flex-col bg-background transition-colors duration-200"
    >
      <div className="relative aspect-[3/4] w-full overflow-hidden bg-muted">
        {product.featured_image_url ? (
          <img
            src={product.featured_image_url}
            alt={product.title}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.03]"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-muted-foreground text-xs uppercase tracking-widest">
            yishengfushi
          </div>
        )}

        {product.on_sale && (
          <div className="absolute top-3 left-3">
            <span className="bg-primary px-2 py-0.5 text-[10px] font-medium tracking-widest text-primary-foreground uppercase">
              {t.sale}
            </span>
          </div>
        )}

        <div className="absolute inset-x-0 bottom-0 p-3 opacity-0 transition-opacity duration-200 group-hover:opacity-100 hidden sm:block">
          <AddToCartButton
            product={product}
            productId={product.id}
            variant="default"
            size="sm"
            text={t.addBag}
            buttonClassName="w-full bg-primary text-primary-foreground text-xs uppercase tracking-wider py-2"
            showQuantity={false}
          />
        </div>
      </div>

      <div className="flex flex-1 flex-col pt-3 pb-2">
        {product.categories && product.categories.length > 0 && (
          <span className="text-[11px] uppercase tracking-widest text-muted-foreground">
            {product.categories[0].name}
          </span>
        )}

        <Link
          to={product.link}
          className="mt-1 text-sm font-medium text-foreground transition-colors hover:text-primary line-clamp-1"
        >
          {product.title}
        </Link>

        <div className="mt-1">
          <ProductPrice product={product} className="text-sm font-medium text-foreground" />
        </div>

        <div className="mt-3 sm:hidden">
          <AddToCartButton
            product={product}
            productId={product.id}
            variant="default"
            size="sm"
            text={t.addBag}
            buttonClassName="w-full bg-primary text-primary-foreground text-xs uppercase tracking-wider py-2"
            showQuantity={false}
          />
        </div>
      </div>
    </article>
  );
}

function SearchResultsContent({
  activeSearchQuery,
  onSelectSuggestion,
  language,
}: {
  activeSearchQuery: string;
  onSelectSuggestion: (term: string) => void;
  language: Language;
}) {
  const t = translations[language];
  const { products, loading, error, refetch, total, hasNext, loadMore, isRefetching } =
    useEcommerceProducts();

  if (loading) {
    return (
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 lg:gap-6">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} data-index={i} className="flex flex-col space-y-3">
            <div className="aspect-[3/4] w-full animate-pulse bg-muted" />
            <div className="h-4 w-2/3 animate-pulse bg-muted" />
            <div className="h-4 w-1/3 animate-pulse bg-muted" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="border border-border bg-card p-8 text-center">
        <p className="text-sm text-muted-foreground">
          Unable to display catalogue results at this moment.
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          className="mt-4 text-xs uppercase tracking-wider"
        >
          Retry Search
        </Button>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="space-y-12">
        <div className="border border-border bg-card p-10 text-center md:p-14">
          <h2 className="font-serif text-2xl font-normal text-foreground md:text-3xl">
            {t.noResults}
          </h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
            {activeSearchQuery
              ? `${t.noMatches} "${activeSearchQuery}". ${t.exploreEdits}`
              : t.refineTerms}
          </p>

          <div className="mt-8">
            <span className="text-xs uppercase tracking-widest text-muted-foreground">
              {t.suggestedTerms}
            </span>
            <div className="mt-3 flex flex-wrap justify-center gap-2">
              {POPULAR_SUGGESTIONS.map((term, i) => (
                <Button
                  key={term}
                  data-index={i}
                  variant="outline"
                  size="sm"
                  onClick={() => onSelectSuggestion(term)}
                  className="border-border text-xs text-foreground hover:border-foreground"
                >
                  {term}
                </Button>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="border-b border-border pb-3">
            <h3 className="font-serif text-xl font-normal text-foreground">
              {t.curatedCategories}
            </h3>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
            {CURATED_CATEGORIES.map((cat, i) => (
              <Link
                key={cat.name}
                to={cat.path}
                data-index={i}
                className="group flex flex-col border border-border bg-card p-4 transition-colors hover:border-foreground"
              >
                <span className="text-sm font-medium text-foreground group-hover:text-primary">
                  {cat.name}
                </span>
                <span className="mt-1 text-xs text-muted-foreground">
                  {cat.count}
                </span>
              </Link>
            ))}
          </div>
        </div>

        <div className="space-y-6 pt-4">
          <div className="flex items-baseline justify-between border-b border-border pb-3">
            <h3 className="font-serif text-xl font-normal text-foreground">
              {t.trendingThisSeason}
            </h3>
            <Link
              to="/ShopPage"
              className="text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            >
              {t.viewFullCollection} <ArrowRight className="size-3" />
            </Link>
          </div>

          <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:gap-6">
            {FALLBACK_TRENDING.map((item, i) => (
              <div key={item.title} data-index={i} className="group flex flex-col">
                <div className="aspect-[3/4] w-full overflow-hidden bg-muted">
                  <img
                    src={item.img}
                    data-wvc-srcset={item.srcset}
                    data-wvc-sizes={item.sizes}
                    alt={item.title}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.03]"
                  />
                </div>
                <div className="pt-3">
                  <span className="text-[11px] uppercase tracking-widest text-muted-foreground">
                    {item.category}
                  </span>
                  <p className="mt-1 text-sm font-medium text-foreground line-clamp-1">
                    {item.title}
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {item.price}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 lg:gap-6">
        {products.map((product, i) => (
          <ProductGridCard
            key={product.id}
            product={product}
            index={i}
            language={language}
          />
        ))}
      </div>

      {hasNext && (
        <div className="pt-6 text-center">
          <Button
            variant="outline"
            size="lg"
            onClick={async () => await loadMore()}
            disabled={isRefetching}
            className="border-border px-8 text-xs uppercase tracking-widest hover:border-foreground"
          >
            {isRefetching ? t.loading : t.loadMore}
          </Button>
        </div>
      )}
    </div>
  );
}

export default function CatalogSearch() {
  const language = useWvcLanguage();
  const t = translations[language];

  const [searchValue, setSearchValue] = useState("");
  const [submittedQuery, setSubmittedQuery] = useState("");
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const activeWPQuery = useMemo(() => {
    const term = submittedQuery.trim();
    if (term) {
      return new WP_Query({ s: term, post_type: "product" });
    }
    if (
      typeof window !== "undefined" &&
      typeof (window as any).wvcClient !== "undefined" &&
      typeof (window as any).wvcClient.getCurrentWPQueryParams === "function"
    ) {
      const currentParams = (window as any).wvcClient.getCurrentWPQueryParams();
      return new WP_Query(currentParams);
    }
    return new WP_Query({ post_type: "product", per_page: 12 });
  }, [submittedQuery]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmittedQuery(searchValue.trim());
  };

  const handleSuggestionClick = (term: string) => {
    setSearchValue(term);
    setSubmittedQuery(term);
  };

  const handleClear = () => {
    setSearchValue("");
    setSubmittedQuery("");
  };

  return (
    <section
      id="搜索框与结果"
      data-nav="light"
      className="bg-background text-foreground py-10 md:py-16"
    >
      <div className="mx-auto max-w-[1360px] px-6 md:px-10 lg:px-16">
        {/* Top Header & Search Area */}
        <div className="mx-auto max-w-3xl text-center">
          <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">
            {t.catalogueSearch}
          </span>
          <h1 className="mt-2 font-serif text-3xl font-normal tracking-tight text-foreground md:text-5xl">
            {t.findSignature}
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            {t.searchDescription}
          </p>

          <form
            onSubmit={handleSearchSubmit}
            className="relative mt-8 flex items-center shadow-xs"
          >
            <div className="pointer-events-none absolute left-4 text-muted-foreground">
              <Search className="size-5" />
            </div>
            <Input
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder={t.searchPlaceholder}
              className="h-12 w-full border-border bg-background pl-12 pr-28 text-sm text-foreground placeholder:text-muted-foreground focus-visible:border-foreground focus-visible:ring-0 rounded-none"
            />
            {searchValue && (
              <Button
                type="button"
                variant="ghost"
                onClick={handleClear}
                className="absolute right-24 p-1 h-auto text-muted-foreground hover:text-foreground hover:bg-transparent"
                aria-label="Clear search"
              >
                <X className="size-4" />
              </Button>
            )}
            <Button
              type="submit"
              variant="default"
              size="default"
              className="absolute right-1 top-1 bottom-1 h-auto bg-secondary px-5 text-xs uppercase tracking-wider text-secondary-foreground hover:bg-secondary/90 rounded-none"
            >
              {t.search}
            </Button>
          </form>

          {/* Quick suggestions pills */}
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
            <span className="text-xs text-muted-foreground">{t.popular}</span>
            {POPULAR_SUGGESTIONS.map((term, i) => (
              <Button
                key={term}
                data-index={i}
                type="button"
                variant="link"
                onClick={() => handleSuggestionClick(term)}
                className="h-auto p-0 text-xs text-foreground/80 underline-offset-4 hover:underline hover:text-primary transition-colors cursor-pointer"
              >
                {term}
                {i < POPULAR_SUGGESTIONS.length - 1 ? "," : ""}
              </Button>
            ))}
          </div>
        </div>

        {/* Results Container with EcommerceProductsProvider */}
        <div className="mt-12 md:mt-16 border-t border-border pt-8">
          <EcommerceProductsProvider wp_query={activeWPQuery}>
            <div className="flex flex-col lg:flex-row lg:gap-8">
              {/* Desktop Filter Sidebar */}
              <aside className="hidden w-1/4 shrink-0 lg:block">
                <div className="sticky top-24 border-r border-border pr-6">
                  <div className="mb-4 flex items-center justify-between border-b border-border pb-3">
                    <span className="text-xs font-semibold uppercase tracking-wider text-foreground">
                      {t.refineSearch}
                    </span>
                  </div>
                  <ProductsFilter />
                </div>
              </aside>

              {/* Main Results Column */}
              <div className="w-full lg:w-3/4 space-y-6">
                {/* Result metadata & controls */}
                <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border pb-4">
                  <div>
                    <h2 className="text-sm font-medium text-foreground">
                      {submittedQuery ? (
                        <>
                          {t.searchResults}{" "}
                          <span className="italic font-serif text-base">
                            "{submittedQuery}"
                          </span>
                        </>
                      ) : (
                        t.allCollections
                      )}
                    </h2>
                  </div>

                  <div className="flex items-center gap-3">
                    {/* Mobile filter trigger */}
                    <div className="lg:hidden">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setMobileFilterOpen(!mobileFilterOpen)}
                        className="flex items-center gap-1.5 text-xs uppercase tracking-wider border-border"
                      >
                        <SlidersHorizontal className="size-3.5" />
                        Filters
                      </Button>
                    </div>

                    <div className="w-44">
                      <ProductSortDropdown label="Sort" />
                    </div>
                  </div>
                </div>

                {/* Mobile Filter Drawer / Expansion */}
                {mobileFilterOpen && (
                  <div className="border border-border bg-card p-4 lg:hidden">
                    <div className="mb-3 flex items-center justify-between border-b border-border pb-2">
                      <span className="text-xs font-semibold uppercase tracking-wider">
                        {t.filterCriteria}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setMobileFilterOpen(false)}
                        className="h-6 p-1 text-xs"
                      >
                        {t.close}
                      </Button>
                    </div>
                    <ProductsFilter />
                  </div>
                )}

                {/* Dynamic Product Grid Consumer */}
                <SearchResultsContent
                  activeSearchQuery={submittedQuery}
                  onSelectSuggestion={handleSuggestionClick}
                  language={language}
                />
              </div>
            </div>
          </EcommerceProductsProvider>
        </div>
      </div>
    </section>
  );
}